


import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { logout } from "../store/slices/authSlice";
import Sidebar from "./Sidebar";
import SidebarToggle from "./SidebarToggle";
import { Wallet, ChevronDown, LogOut, Settings } from "lucide-react";
import logo from "../assets/logo.png";
import UpperTab from "./UpperTab";
import FooterSection from "./Footer/FooterSection";
import { useSidebar } from "../context/SidebarContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import ChangePasswordModal from './ChangePasswordModal';
import { socketService } from "../services/socket";
import { setCredentials } from "../store/slices/authSlice";

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [isMobile, setMobile] = useState(window.innerWidth < 1024);

  // Get user data from Redux instead of API
  const { user } = useSelector((state) => state.auth);

  const [localUserObj, setLocalUserObj] = useState(null);
  useEffect(() => {
    try {
      const l = localStorage.getItem('user');
      if (l && l !== 'null' && l !== 'undefined') setLocalUserObj(JSON.parse(l));
    } catch (e) { console.error(e); }
  }, []);

  const effectiveUser = user;

  const [currentTime, setCurrentTime] = useState(new Date());
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState('IST');



  // Time zones configuration
  const timezones = [
    { code: 'GMT', name: 'System time', offset: '+00:00', label: '-(GMT +00:00)' },
    { code: 'IST', name: 'Your computer time', offset: '+05:30', label: '-(GMT +05:30)' },
    { code: 'IST_STANDARD', name: 'India Standard time', offset: '+05:30', label: '-(GMT +05:30)' }
  ];

  /* ──────────────────────────────  auto-logout on token expiry  */
  useEffect(() => {
    const activeRole = localStorage.getItem('activeRole')
    const token = activeRole === 'owner'
      ? localStorage.getItem('ownerToken')
      : activeRole === 'staff'
        ? localStorage.getItem('staffToken')
        : localStorage.getItem('token')

    if (!token) return

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      if (!payload.exp) return

      const expiresAt = payload.exp * 1000
      const now = Date.now()
      const timeUntilExpiry = expiresAt - now

      if (timeUntilExpiry <= 0) {
        console.log('Token already expired, logging out...')
        handleLogout()
        return
      }

      // Live countdown in console every 30 seconds
      const countdownInterval = setInterval(() => {
        const remaining = expiresAt - Date.now()
        if (remaining <= 0) {
          clearInterval(countdownInterval)
          return
        }
        const m = Math.floor(remaining / 60000)
        const s = Math.floor((remaining % 60000) / 1000)
        console.log(`⏱ Token expires in: ${m}m ${s}s`)
      }, 30000)

      // Initial log
      const m = Math.floor(timeUntilExpiry / 60000)
      const s = Math.floor((timeUntilExpiry % 60000) / 1000)
      console.log(`⏱ Token expires in: ${m}m ${s}s | Expires at: ${new Date(expiresAt).toLocaleTimeString()}`)

      // Auto-logout timer
      const logoutTimer = setTimeout(() => {
        console.log('Token expired, auto-logging out...')
        clearInterval(countdownInterval)
        handleLogout()
      }, timeUntilExpiry)

      return () => {
        clearTimeout(logoutTimer)
        clearInterval(countdownInterval)
      }
    } catch (e) {
      console.warn('Could not parse token for auto-logout:', e)
    }
  }, [])

  /* ──────────────────────────────  handle resize  */
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 1024;
      setMobile(mobile);
      console.log("Resize: isMobile =", mobile);
      // Don't auto-toggle sidebar on resize - let user control it
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ──────────────────────────────  time updates  */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  /* ──────────────────────────────  Auth Redirect Check  */
  // useEffect(() => {
  //   // Check if we are on a protected page (Layout is usually for protected pages)
  //   // and if the user/token is missing from both Redux and LocalStorage
  //   const token = localStorage.getItem('token') || localStorage.getItem('ownerToken') || localStorage.getItem('staffToken');
  //   const localUser = localStorage.getItem('user');

  //   if ((!user && !token && !localUser) || (localUser === 'null') || (localUser === 'undefined')) {
  //     console.log("No user/token found, redirecting to login");
  //     navigate("/login");
  //   }
  // }, [user, navigate]);

  // useEffect(() => {
  //   // If we have a user object (from redux or local) but it lacks identity, redirect
  //   if (effectiveUser && !effectiveUser.username && !effectiveUser.name) {
  //     navigate("/login");
  //   }
  // }, [effectiveUser, navigate]);

  // Socket Connection & Profile Subscription
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('ownerToken') || localStorage.getItem('staffToken');

    if (token) {
      // Connect to socket
      socketService.connect(token);

      // Subscribe to profile updates
      const unsubscribe = socketService.subscribeToAdminProfile(token, (data) => {
        if (data && data.success && data.data) {
          // We received new profile data
          // Update Redux state
          // Note: API returns { success: true, data: { user: ... } }
          // Our Redux expects { user: ..., token: ... } or just updating user part
          // But setCredentials expects { user, token, role }

          const currentUser = effectiveUser || {};
          const newUser = { ...currentUser, ...data.data.user };

          // Preserve role if not in update (though it should be)
          const role = newUser.role || currentUser.role;

          dispatch(setCredentials({
            user: newUser,
            token: token,
            role: role
          }));
        } else if (data && data.error) {
          console.error('Socket Profile Error:', data.error);
        }
      });

      return () => {
        if (unsubscribe) unsubscribe();
        // We don't disconnect here because Layout dictates the whole auth session, 
        // but if we navigate away from Layout (e.g. to Login), we should disconnect.
        // However, connect() is singleton-safe.
      };
    }
  }, [dispatch, effectiveUser]); // Depend on token implicitly or fetch it inside

  // Disconnect on unmount (e.g. logout)
  useEffect(() => {
    return () => {
      // If we are truly unmounting Layout (logout or app close), disconnect
      // socketService.disconnect(); 
      // Actually, let's leave it managed by the service or explicit logout
    };
  }, []);

  const handleLogout = () => {
    // Save adminUsers before clearing
    const admins = localStorage.getItem("adminUsers");

    // Disconnect socket
    socketService.disconnect();

    // Clear React Query cache
    queryClient.clear();

    // Dispatch Redux logout action to clear auth state
    dispatch(logout());

    // Clear all localStorage except adminUsers
    localStorage.clear();
    if (admins) {
      localStorage.setItem("adminUsers", admins);
    }

    // Clear all session storage
    sessionStorage.clear();

    // Navigate to login page
    navigate("/login");
  };

  /* ──────────────────────────────  time formatting  */
  const formatDateTime = (date, timezone) => {
    let targetDate = new Date(date);
    if (timezone === 'GMT') {
      const utcTime = targetDate.getTime() + (targetDate.getTimezoneOffset() * 60000);
      targetDate = new Date(utcTime);
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = targetDate.getDate();
    const month = months[targetDate.getMonth()];
    const year = targetDate.getFullYear();
    const hours = targetDate.getHours().toString().padStart(2, '0');
    const minutes = targetDate.getMinutes().toString().padStart(2, '0');
    const seconds = targetDate.getSeconds().toString().padStart(2, '0');
    return `${month} ${day}, ${year} ${hours}:${minutes}:${seconds}`;
  };

  const getTimezoneOffset = (timezone) => {
    const tz = timezones.find(t => t.code === timezone);
    return tz ? tz.offset : '+05:30';
  };

  const formatLastLogin = () => {
    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  /* ──────────────────────────────  helpers  */
  const pageTitle = (() => {
    const seg = location.pathname.split("/").filter(Boolean).pop() || "dashboard";
    return seg
      .replace(/-/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, s => s.toUpperCase());
  })();

  const handleToggleSidebar = () => {
    toggleSidebar();
    console.log("Sidebar toggled: isSidebarOpen =", !isSidebarOpen);
  };

  /* ──────────────────────────────  render  */
  return (
    <div className="flex flex-col h-screen overflow-y-scroll bg-[#222951]">
      {/* Header at the top */}
      <header style={{ zIndex: 9999 }} className="bg-[#222951] px-4 py-2 flex flex-wrap justify-between items-center z-10 border-b-4 border-[#333d79] flex-shrink-0">
        {/* Left - Sidebar toggle + Logo + Timezone */}
        <div className="flex items-center space-x-3 flex-grow-0">
          <button
            className="lg:hidden text-gray-300"
            onClick={handleToggleSidebar}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img
            onClick={() => navigate("/dashboardhome")}
            src={logo}
            alt="Logo"
            className="h-12 w-auto md:h-[60px] transition-all duration-300"
          />
          <div className="relative ml-4 hidden md:block flex-shrink-0">
            <button
              className="flex items-center space-x-2 text-white hover:bg-[#34495e] px-3 py-1 rounded transition-colors"
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
            >
              <span className="text-white text-sm font-medium ml-4">
                {(() => {
                  const dateTimeStr = formatDateTime(currentTime, selectedTimezone);
                  const parts = dateTimeStr.split(' ');
                  const datePart = parts.slice(0, 3).join(' '); // "Jul 29, 2025"
                  const timePart = parts[3]; // "23:27:38"

                  return (
                    <>
                      {datePart} <span className="font-bold text-[20px]">{timePart}</span> <span className="text-[18px]">({getTimezoneOffset(selectedTimezone)})</span>
                    </>
                  );
                })()}
              </span>
              <ChevronDown className={`w-4 h-4 text-white transition-transform ${showTimeDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showTimeDropdown && (
              <div className="absolute top-full mt-1 left-1/2 transform -translate-x-1/2 bg-white rounded-sm shadow-lg py-1 min-w-80 z-50">
                {/* Arrow pointing up */}
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white"></div>
                {timezones.map((tz) => (
                  <button
                    key={tz.code}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex justify-between items-center border-b border-gray-100 last:border-b-0"
                    onClick={() => {
                      setSelectedTimezone(tz.code);
                      setShowTimeDropdown(false);
                    }}
                  >
                    <span className="text-gray-900">{tz.name}</span>
                    <span className="text-gray-600 text-sm">{tz.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right - User Info and Logout */}
        <div className="flex items-center space-x-4 flex-grow-0 ml-auto">
          <div className="hidden font-semibold md:flex text-white text-left flex-col flex-shrink-0 mr-2">
            <div className="text-sm">
              Logged in as{' '}
              <span className="text-white">
                {effectiveUser?.username || effectiveUser?.name || ''}/{effectiveUser?.role}
              </span>
            </div>
            <div className="text-white text-[12px] truncate">
              Last logged in: {formatLastLogin()}
            </div>
          </div>
          <div className="flex items-center space-x-4 flex-grow-0 ml-auto">
            {/* Settings Dropdown */}
            <div className="relative">
              <button
                className="flex items-center text-white hover:bg-[#34495e] px-3 py-1 rounded transition-colors"
                onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              >
                <FontAwesomeIcon icon={faCog} className="w-4 h-4 mr-1" />
                <span className="text-sm font-semibold">Settings</span>
              </button>

              {showSettingsDropdown && (
                <div className="absolute top-full mt-1 right-0 bg-white rounded-sm shadow-lg py-1 min-w-48 z-50">
                  {/* Arrow pointing up */}
                  <div className="absolute -top-2 right-16 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white"></div>
                  <button
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center border-b border-gray-100"
                    onClick={() => {
                      setShowSettingsDropdown(false);
                      setChangePasswordModalOpen(true);
                    }}
                  >
                    <span className="text-black">Change Password</span>
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center"
                    onClick={() => {
                      setShowSettingsDropdown(false);
                      navigate('/secureauth');
                    }}
                  >
                    <span className="text-black">Secure verification</span>
                  </button>
                </div>
              )}
            </div>

            <button
              className="flex items-center text-white hover:bg-[#34495e] px-3 py-1 rounded transition-colors"
              onClick={handleLogout}
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4 mr-1" />
              <span className="text-sm font-semibold">LogOut</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content area below header - Sidebar and Main Content */}
      <div className="flex flex-1 relative">
        {/* Sidebar */}
        {isSidebarOpen && (
          <div className="w-[180px] flex-shrink-0">
            <Sidebar toggleSidebar={handleToggleSidebar} />
          </div>
        )}

        {/* Separate Sidebar Toggle Button */}
        <SidebarToggle />

        {/* Main Content Area */}
        <div className="flex flex-col transition-all duration-300 flex-1 min-w-0">
          <div style={{ paddingLeft: '22px' }} className="w-full  pt-2">
            <UpperTab />
          </div>
          <main className="flex-1 w-full">
            <div style={{ paddingLeft: '22px' }} className="w-full  pt-2">
              {children || <Outlet />}
            </div>
          </main>
        </div>
      </div>

      {/* Footer placed below content */}
      <FooterSection />

      {/* Overlay elements */}
      {showTimeDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowTimeDropdown(false)}
        />
      )}
      {showSettingsDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowSettingsDropdown(false)}
        />
      )}

      s
      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
      />
    </div>
  );
};

export default Layout;